/**
 * Whitelabel Variant Configuration
 *
 * This file defines the type-safe variant system used throughout the application.
 * Variants allow for whitelabel customization by overriding pages and components
 * with variant-specific versions using file extensions (e.g., page.cada.tsx).
 */

/**
 * Union type of all supported variants.
 * Add new variants here as needed.
 */
export type Variant = "nada" | "cada" | "atd";

/**
 * Type-safe variant constants to prevent typos.
 * Use these constants instead of string literals throughout the codebase.
 */
export const VARIANTS = {
    NADA: "nada" as const,
    CADA: "cada" as const,
    ATD: "atd" as const,
} as const;

/**
 * Array of all valid variant names.
 * Useful for validation and iteration.
 */
export const VALID_VARIANTS: readonly Variant[] = Object.values(VARIANTS);

/**
 * Gets the current variant from the environment variable NEXT_PUBLIC_VARIANT.
 * Defaults to "nada" if not set or invalid.
 *
 * @returns The current variant
 */
export function getCurrentVariant(): Variant {
    const envVariant = process.env.NEXT_PUBLIC_VARIANT;

    // Priority 1: If NEXT_PUBLIC_VARIANT is set (for local testing), use it
    if (envVariant && isValidVariant(envVariant)) {
        return envVariant;
    }

    // Priority 2: Check hostname for variant-specific domains (client-side only)
    if (typeof window !== "undefined") {
        const hostname = window.location.hostname;

        if (hostname.includes("cada")) {
            return VARIANTS.CADA;
        }

        if (hostname.includes("atd")) {
            return VARIANTS.ATD;
        }
    }

    // Default to NADA variant
    return VARIANTS.NADA;
}

/**
 * Type guard to check if a string is a valid variant.
 *
 * @param value - The value to check
 * @returns True if the value is a valid variant
 */
export function isValidVariant(value: unknown): value is Variant {
    return typeof value === "string" && VALID_VARIANTS.includes(value as Variant);
}

/**
 * Validates that the NEXT_PUBLIC_VARIANT environment variable is set correctly.
 * Throws an error if invalid in non-production environments.
 * Logs a warning in production to avoid breaking the build.
 */
export function validateVariantConfig(): void {
    const envVariant = process.env.NEXT_PUBLIC_VARIANT;

    if (!envVariant) {
        const message =
            "NEXT_PUBLIC_VARIANT environment variable is not set. Defaulting to 'nada'.";
        if (process.env.NODE_ENV === "production") {
            console.warn(message);
        } else {
            console.info(message);
        }
        return;
    }

    if (!isValidVariant(envVariant)) {
        const message = `Invalid NEXT_PUBLIC_VARIANT: "${envVariant}". Must be one of: ${VALID_VARIANTS.join(", ")}. Defaulting to 'nada'.`;
        if (process.env.NODE_ENV === "production") {
            console.warn(message);
        } else {
            console.error(message);
        }
    }
}
